/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import KEYS from '../../language/keys';
import {
  ANALYTICS_EVENTS, COMPONENT_NAME, ELEMENT_TYPE, EVENT_GROUP, PRODUCT_AREA, UPGRADE_TO_PRO,
} from '../../consts/analytics';
import { useAnalyticsContext } from '../../contexts/analytics/useAnalyticsContext';
import { AnalyticsProvider } from '../../contexts/analytics';
import { AppRouting } from '../../services/appRoutingResolver';
import { CONFIG } from '../../config';
import { INTERNAL_SERVER_ERROR_CODES } from '../../consts/api';
import UpgradeToProDialog
  from '../../components/dialogs/time-to-pay-dialogs/upgrade-to-pro-dialog/upgrade-to-pro-dialog.component';
import { PricingPlanEnum } from '../../enums/user-plan.enum';
import { PAGE_NAMES } from '../../consts/pages';
import { AlertSnackBar } from '../../components/Common/CustomSnackBar/AlertSnackBar';
import { useGetMyAccountSelector } from '../../store/rtk-query/apis/account/selectors/get-account-by-id.selector';
import UpgradeToEnterpriseDialog
  from '../../components/dialogs/time-to-pay-dialogs/upgrade-to-enterprise-dialog/upgrade-to-enterprise-dialog.component';
import { useCreatePositionStoreMetadataSelector } from '../../store/slices/create-position-state/selectors/create-position.selector';
import { resetError } from '../../store/slices/create-position-state/create-position-state.toolkit-slice';
import { usePaidPlanRequestMutation } from '../../store/rtk-query/apis/account-plan/hooks/paid-plan-request.mutation-hook';
import CreatePositionDialog from './components/create-position-dialog/create-position-dialog.component';
import { CREATE_POSITION_ANALYTICS_EVENTS } from './consts/position-analytics.consts';

type CreatePositionsProps = {
  isCreatePositionDialogOpen: boolean,
  positionQuotaExceededError: boolean,
  setIsOpen: (value: boolean) => void;
}

function CreatePosition({
  isCreatePositionDialogOpen,
  setIsOpen,
  positionQuotaExceededError,
}: CreatePositionsProps): JSX.Element {
  const [isAccountUpgradeDialogOpen, setIsAccountUpgradeDialogOpen] = useState(positionQuotaExceededError);
  const { t: translate } = useTranslation();
  const { analyticsTrackEvent } = useAnalyticsContext();
  const dispatch = useDispatch();
  const { error, errorCode } = useCreatePositionStoreMetadataSelector();
  const { data: { plan, paidPlanRequested, id: accountId } } = useGetMyAccountSelector();
  const [
    requestPaidPlan,
    {
      isError: isErrorPaidPlanRequest, isLoading: isLoadingPaidPlanRequest, resetState: resetPaidPlanRequestState,
    },
  ] = usePaidPlanRequestMutation();

  const onDialogClickBIEvent = (type: string) => {
    const clickOnDialogEventData = {
      eventName: ANALYTICS_EVENTS.CLICKED_ON_PREMIUM_MODAL,
      eventGroup: EVENT_GROUP.CLICK,
      productArea: PRODUCT_AREA.ADD_POSITION,
      pageName: PAGE_NAMES.HOME_PAGE,
      componentElementType: type,
      componentName: plan?.pricingPlan !== PricingPlanEnum.LITE ? COMPONENT_NAME.PRO_POSITION_LIMIT : COMPONENT_NAME.LITE_POSITION_LIMIT,
    };

    analyticsTrackEvent(clickOnDialogEventData);
  };

  const onDialogCloseHandler = () => {
    onDialogClickBIEvent(ELEMENT_TYPE.CLOSE);
    setIsOpen(false);
    setIsAccountUpgradeDialogOpen(false);
  };

  useEffect(() => {
    if (isErrorPaidPlanRequest) {
      onDialogCloseHandler();
    }
  }, [isErrorPaidPlanRequest]);

  useEffect(() => {
    setIsAccountUpgradeDialogOpen(positionQuotaExceededError && isCreatePositionDialogOpen);
  }, [positionQuotaExceededError, isCreatePositionDialogOpen]);

  useEffect(() => {
    if (error) {
      const eventData = {
        eventName: CREATE_POSITION_ANALYTICS_EVENTS.NEW_POSITION_FAILED,
        eventGroup: EVENT_GROUP.CLICK,
        pageName: PAGE_NAMES.HOME_PAGE,
        productArea: `${PRODUCT_AREA.ADD_POSITION}/${PRODUCT_AREA.HOMEPAGE}`,
        positionQuota: plan.maximumOpenPositions || 0,
        failReason: error || 'position quota',
      };

      analyticsTrackEvent(eventData);
    }

    if (isCreatePositionDialogOpen && isAccountUpgradeDialogOpen) {
      const eventData = {
        eventName: CREATE_POSITION_ANALYTICS_EVENTS.NEW_POSITION_PREMIUM_MODAL_SHOWN,
        eventGroup: EVENT_GROUP.IMP,
        pageName: PAGE_NAMES.HOME_PAGE,
        productArea: `${PRODUCT_AREA.ADD_POSITION}/${PRODUCT_AREA.HOMEPAGE}`,
        positionQuota: plan.maximumOpenPositions || 0,
        componentName: plan?.pricingPlan !== PricingPlanEnum.LITE ? COMPONENT_NAME.ADD_POSITION_PRO : COMPONENT_NAME.ADD_POSITION_LITE,
      };

      analyticsTrackEvent(eventData);
    }
  }, [isAccountUpgradeDialogOpen, error]);

  const onSnackBarActionClick = () => {
    dispatch(resetError());
    if (CONFIG.BILLING_URL) {
      AppRouting.openInNewTab(CONFIG.BILLING_URL);
    }
  };

  const getSnackTitle = (): string => {
    if (isErrorPaidPlanRequest) {
      return translate(KEYS.GLOBAL_ERROR_MESSAGE);
    }

    if (errorCode === INTERNAL_SERVER_ERROR_CODES.POSITION_INTERNAL_ERROR_CODE_QUOTA || isAccountUpgradeDialogOpen) {
      return translate(KEYS.CREATE_POSITION.ERRORS.TITLE_QUOTA);
    }

    if (errorCode === INTERNAL_SERVER_ERROR_CODES.ACCOUNT_EXPIRED_ERROR_CODE || isAccountUpgradeDialogOpen) {
      return translate(KEYS.CREATE_POSITION.ERRORS.TITLE_EXPIRED);
    }

    return translate(KEYS.CREATE_POSITION.ERRORS.TITLE);
  };

  const getSnackActionTitle = (): string | undefined => {
    if (errorCode === INTERNAL_SERVER_ERROR_CODES.POSITION_INTERNAL_ERROR_CODE_QUOTA || isAccountUpgradeDialogOpen) {
      return translate(KEYS.CREATE_POSITION.ERRORS.ACTION_TITLE);
    }

    return undefined;
  };

  const getSnackActionDescription = (): string | undefined => {
    if (errorCode === INTERNAL_SERVER_ERROR_CODES.POSITION_INTERNAL_ERROR_CODE_QUOTA || isAccountUpgradeDialogOpen) {
      return translate(KEYS.CREATE_POSITION.ERRORS.DESCRIPTION);
    }

    return undefined;
  };

  const onClickUpgradeToPro = () => {
    const goProEventData = {
      eventName: ANALYTICS_EVENTS.CLICKED_ON_GO_PRO,
      eventGroup: EVENT_GROUP.CLICK,
      productArea: PRODUCT_AREA.HOMEPAGE,
      pageName: PAGE_NAMES.HOME_PAGE,
      referral: plan?.pricingPlan !== PricingPlanEnum.LITE ?
        UPGRADE_TO_PRO.MODAL_ADD_POSITION_PRO :
        UPGRADE_TO_PRO.MODAL_ADD_POSITION_LITE,
    };

    onDialogClickBIEvent(ELEMENT_TYPE.GO_PRO);
    analyticsTrackEvent(goProEventData);
  };

  const onAlertClose = () => {
    dispatch(resetError());
    resetPaidPlanRequestState?.();
  };

  const renderUpdateDialog = () => {
    if (!isCreatePositionDialogOpen || !isAccountUpgradeDialogOpen) {
      return <></>;
    }

    if (plan?.pricingPlan !== PricingPlanEnum.LITE) {
      return (
        <UpgradeToEnterpriseDialog
          isOpen={ isCreatePositionDialogOpen && isAccountUpgradeDialogOpen }
          onClose={ onDialogCloseHandler }
          isLoading={ isLoadingPaidPlanRequest }
          paidPlanRequested={ paidPlanRequested }
          actionClickHandler={ () => {
            requestPaidPlan(accountId);
            onClickUpgradeToPro();
          } }
        />
      );
    }

    return (
      <UpgradeToProDialog
        title={ translate(KEYS.UPGRADE_TO_PRO_DIALOG.HEADER_TITLE) }
        subTitle={ translate(KEYS.UPGRADE_TO_PRO_DIALOG.HEADER_SUB_TITLE) }
        isOpen={ isCreatePositionDialogOpen && isAccountUpgradeDialogOpen }
        onClose={ onDialogCloseHandler }
        onActionClick={ onClickUpgradeToPro }
      />
    );
  };

  return (
    <>
      <AnalyticsProvider productArea={ PRODUCT_AREA.ADD_POSITION }>
        <CreatePositionDialog
          isOpen={ isCreatePositionDialogOpen && !positionQuotaExceededError }
          setIsOpen={ setIsOpen }
        />
        <AlertSnackBar
          shouldAutoDismiss
          isOpen={ !!error || isErrorPaidPlanRequest }
          title={ getSnackTitle() }
          actionTitle={ getSnackActionTitle() }
          description={  getSnackActionDescription() }
          onActionClick={ onSnackBarActionClick }
          onClose={ onAlertClose }
        />
        { renderUpdateDialog() }
      </AnalyticsProvider>
    </>
  );
}

export default CreatePosition;
